import { ReadonlyVec2 } from 'gl-matrix';
import { View } from '../../../view/View';
import { PlaneView } from '../../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ToolAction } from '../../ToolAction';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';

/**
 * It stores the state of the active {@link BrushCursorAction}.\
 * {@link BrushCursorActionState} is constructed when the {@link BrushCursorAction} starts. And deleted when the {@link BrushCursorAction} finishes.\
 * {@link BrushCursorAction} starts when the user moves their cursor over a {@link PlaneView}.\
 * It finishes when the user moves their mouse out of the {@link PlaneView} or starts drawing.\
 * {@link BrushCursorActionState} is used to render the brush cursor onto the screen.
 */
export abstract class BrushCursorActionState {
    /**
     * Instance of generic type {@link T}, which is a {@link CustomObject} presentation of the brush cursor.
     */
    protected readonly cursorObject: CustomTargetObject;
    protected readonly _planeView: PlaneView;

    public get view(): PlaneView {
        return this._planeView;
    }

    /**
     * {@link BrushCursorActionState} constructor, which takes the {@link PlaneView} on which the mouse is located,\
     * the {@link color} of the brush and the {@link ReadonlyVec2 client pixel coordinates} of the mouse at the time of the event.
     * @param view {@link PlaneView} on which the mouse is located.
     * @param color {@link ReadonlyVec3} defining the color of the brush.
     * @param coordinates {@link ReadonlyVec2 client pixel coordinates} of the mouse at the time of the event.
     * @param cursorObject {@link CustomObject} that is the model presentation of the brush cursor.
     */
    public constructor(view: PlaneView, cursorObject: CustomTargetObject) {
        this._planeView = view;
        this.cursorObject = cursorObject;
        view.resetCursor();
    }

    public abstract setMousePosition(mousePosition?: ReadonlyVec2): void;

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        view: View
    ): EventHandlerResult {
        if (view === this._planeView) {
            this.setMousePosition(coordinates);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        this.setMousePosition(undefined);
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public clearMemory() {
        this.cursorObject.delete();
    }
}

export abstract class BrushCursorAction extends ToolAction {
    protected brushSize: number;

    public constructor(brushSize: number) {
        super();
        this.brushSize = brushSize;
    }

    /**
     * Method to update the diameter of the brush stored within {@link StaticBrushCursorAction} and {@link StaticBrushCursorActionState}.
     * @param brushSize The new diameter of the brush in millimeters.
     */
    public updateBrushSize(brushSize: number): void {
        this.brushSize = brushSize;
    }

    public stopAction(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
